import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Tabs } from '../tabs/Tabs'
import { DayOfWeek, DayOfWeekOrder } from '../../utils/dateUtils'

type DaysMode = 'all' | 'weekdays' | 'weekend' | 'custom'

type WeekdaySelectorProps = {
  days: DayOfWeek[]
  disabled?: boolean
  label?: string
  onChange: (days: DayOfWeek[]) => void
}

const weekendDays = [DayOfWeek.SATURDAY, DayOfWeek.SUNDAY]
const weekdayDays = DayOfWeekOrder.slice(0, 5)
const dayPresets = {
  all: DayOfWeekOrder,
  weekdays: weekdayDays,
  weekend: weekendDays,
}

const matchesDays = (selectedDays: DayOfWeek[], days: DayOfWeek[]) =>
  selectedDays.length === days.length && days.every(day => selectedDays.includes(day))

const getDaysMode = (selectedDays: DayOfWeek[]): DaysMode => {
  if (matchesDays(selectedDays, DayOfWeekOrder)) {
    return 'all'
  }
  if (matchesDays(selectedDays, weekdayDays)) {
    return 'weekdays'
  }
  if (matchesDays(selectedDays, weekendDays)) {
    return 'weekend'
  }
  return 'custom'
}

/** Weekday picker with presets (every day, weekdays, weekend) used by pass products and price rules. */
export const WeekdaySelector = ({ days, disabled = false, label, onChange }: WeekdaySelectorProps) => {
  const { t } = useTranslation('common')
  const [touched, setTouched] = useState(false)
  const [mode, setMode] = useState<DaysMode>(() => getDaysMode(days))

  useEffect(() => {
    if (!touched) {
      setMode(getDaysMode(days))
    }
  }, [days, touched])

  const handleDayToggle = (selectedDay: DayOfWeek) => {
    setTouched(true)
    setMode('custom')
    onChange(
      days.includes(selectedDay)
        ? days.filter(day => day !== selectedDay)
        : DayOfWeekOrder.filter(day => day === selectedDay || days.includes(day))
    )
  }

  const handleModeChange = (value: string) => {
    const nextMode = value as DaysMode
    setMode(nextMode)
    if (nextMode !== 'custom') {
      setTouched(true)
      onChange(dayPresets[nextMode])
    }
  }

  return (
    <div className="field weekday-field" role="group" aria-label={label ?? t('weekday-selector.label')}>
      {label && <span className="eyebrow">{label}</span>}
      <div className="weekday-heading">
        <Tabs
          tab={mode}
          tabs={[
            { value: 'all', label: t('weekday-selector.all') },
            {
              value: 'weekdays',
              label: t('weekday-selector.weekdays'),
              mobileLabel: t('weekday-selector.weekdays-short'),
            },
            {
              value: 'weekend',
              label: t('weekday-selector.weekend'),
              mobileLabel: t('weekday-selector.weekend-short'),
            },
            { value: 'custom', label: t('weekday-selector.custom') },
          ]}
          disabled={disabled}
          onChange={handleModeChange}
        />
      </div>
      <div className="tag-row">
        {DayOfWeekOrder.map(day => {
          const selected = days.includes(day)
          return (
            <button
              key={day}
              type="button"
              className={`badge ${selected ? 'badge-accent' : ''}`}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => handleDayToggle(day)}
            >
              <span className="d-none d-sm-inline">{t(`days.${day.toLowerCase()}`)}</span>
              <span className="d-sm-none">{t(`days-short.${day.toLowerCase()}`)}</span>
            </button>
          )
        })}
      </div>
      {touched && days.length === 0 && <div className="input-error">{t('weekday-selector.required')}</div>}
    </div>
  )
}
